import React from 'react';
import Header from '../components/Header';
import { Shield, Users, Smartphone, FileText, CheckCircle, ArrowRight, BookOpen, AlertTriangle, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Seo } from '../src/seo/Seo';

const CGU: React.FC = () => {
  // Fonctions par défaut pour les props du Header
  const handleDownloadClick = () => {
    // Rediriger vers la page d'accueil pour le téléchargement
    window.location.href = '/';
  };

  const handleMySpaceClick = () => {
    // Rediriger vers la page d'accueil pour My Space
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Seo
        title={{
          fr: "Conditions Générales d'Utilisation (CGU) — Burok",
          en: 'Terms of service — Burok',
        }}
        description={{
          fr: "Consultez les Conditions Générales d'Utilisation de Burok: utilisation du service, droits, responsabilités et sécurité.",
          en: 'Read Burok terms of service: service usage, rights, responsibilities, and security.',
        }}
        canonicalPath={{ fr: '/cgu', en: '/en/cgu' }}
      />
      <Header onDownloadClick={handleDownloadClick} onMySpaceClick={handleMySpaceClick} />
      
      {/* Hero Section */}
      <section className="px-4 pt-20 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex justify-center items-center mb-6 w-16 h-16 bg-blue-100 rounded-full">
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl">
            Conditions Générales d'Utilisation
          </h1>
          <p className="mx-auto max-w-2xl text-xl text-gray-600">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="p-8 bg-white rounded-2xl shadow-xl md:p-12">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="flex items-center mb-4 text-2xl font-bold text-gray-900">
                <CheckCircle className="mr-3 w-6 h-6 text-green-500" />
                Introduction
              </h2>
              <p className="leading-relaxed text-gray-700">
                Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation de l'application mobile Burok. 
                En téléchargeant et en utilisant l'application, vous acceptez d'être lié par ces conditions.
              </p>
            </div>

            {/* Service Description */}
            <div className="mb-12">
              <h2 className="flex items-center mb-4 text-2xl font-bold text-gray-900">
                <Smartphone className="mr-3 w-6 h-6 text-blue-500" />
                Description du Service
              </h2>
              <p className="mb-4 leading-relaxed text-gray-700">
                Burok est une application mobile qui vous permet de gérer vos finances personnelles de manière simple et intuitive. 
                L'application offre des fonctionnalités de suivi des dépenses, de budgétisation et d'analyse financière.
              </p>
              <div className="grid gap-6 mt-6 md:grid-cols-2">
                <div className="p-6 bg-blue-50 rounded-xl">
                  <h3 className="mb-2 font-semibold text-blue-900">Fonctionnalités principales</h3>
                  <ul className="space-y-2 text-blue-800">
                    <li>• Suivi des dépenses en temps réel</li>
                    <li>• Création et gestion de budgets</li>
                    <li>• Analyses et rapports financiers</li>
                    <li>• Synchronisation multi-appareils</li>
                  </ul>
                </div>
                <div className="p-6 bg-purple-50 rounded-xl">
                  <h3 className="mb-2 font-semibold text-purple-900">Compatibilité</h3>
                  <ul className="space-y-2 text-purple-800">
                    <li>• iOS 13.0 et versions ultérieures</li>
                    <li>• Android 8.0 et versions ultérieures</li>
                    <li>• Connexion Internet requise</li>
                    <li>• Compte utilisateur obligatoire</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* User Obligations */}
            <div className="mb-12">
              <h2 className="flex items-center mb-4 text-2xl font-bold text-gray-900">
                <Users className="mr-3 w-6 h-6 text-indigo-500" />
                Obligations de l'Utilisateur
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-3 mr-4 w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <p className="text-gray-700">
                    Vous vous engagez à fournir des informations exactes et à jour lors de la création de votre compte.
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-3 mr-4 w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <p className="text-gray-700">
                    Vous êtes responsable de la confidentialité de vos identifiants de connexion.
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-3 mr-4 w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <p className="text-gray-700">
                    Vous vous engagez à utiliser l'application conformément à sa destination et à la législation en vigueur.
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-3 mr-4 w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <p className="text-gray-700">
                    Vous ne devez pas tenter de contourner les mesures de sécurité de l'application.
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy and Security */}
            <div className="mb-12">
              <h2 className="flex items-center mb-4 text-2xl font-bold text-gray-900">
                <Shield className="mr-3 w-6 h-6 text-green-500" />
                Confidentialité et Sécurité
              </h2>
              <p className="mb-4 leading-relaxed text-gray-700">
                La protection de vos données personnelles est une priorité pour Burok. Nous mettons en œuvre des mesures 
                de sécurité techniques et organisationnelles appropriées pour protéger vos informations.
              </p>
              <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                <h3 className="mb-3 font-semibold text-green-900">Mesures de sécurité</h3>
                <ul className="space-y-2 text-green-800">
                  <li>• Chiffrement des données en transit et au repos</li>
                  <li>• Authentification sécurisée multi-facteurs</li>
                  <li>• Sauvegardes régulières et sécurisées</li>
                  <li>• Surveillance continue des systèmes</li>
                </ul>
              </div>
            </div>

            {/* Intellectual Property */}
            <div className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">
                Propriété Intellectuelle
              </h2>
              <p className="leading-relaxed text-gray-700">
                L'application Burok et son contenu (code source, design, marques, etc.) sont protégés par les droits 
                de propriété intellectuelle. Vous bénéficiez d'une licence d'utilisation limitée, non exclusive et révocable.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">
                Limitation de Responsabilité
              </h2>
              <p className="leading-relaxed text-gray-700">
                Burok s'efforce de fournir un service de qualité mais ne peut garantir une disponibilité continue 
                ou l'absence d'erreurs. Dans la limite autorisée par la loi, notre responsabilité est limitée aux 
                dommages directs prouvés.
              </p>
            </div>

            {/* Termination */}
            <div className="mb-12">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">
                Résiliation
              </h2>
              <p className="leading-relaxed text-gray-700">
                Vous pouvez résilier votre compte à tout moment depuis les paramètres de l'application. 
                Burok se réserve le droit de suspendre ou résilier votre accès en cas de violation des présentes CGU.
              </p>
            </div>

            {/* CGU Complete Section */}
            <div className="mb-12">
              <div className="p-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="mb-8 text-center">
                  <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-blue-100 rounded-full">
                    <BookOpen className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="mb-4 text-3xl font-bold text-gray-900">
                    Consultez le CGU Complet
                  </h2>
                  <p className="mx-auto max-w-2xl text-xl text-gray-600">
                    Pour une compréhension approfondie de vos droits et obligations, 
                    consultez nos conditions générales d'utilisation détaillées.
                  </p>
                </div>

                <div className="grid gap-6 mb-8 md:grid-cols-3">
                  <Link
                    to="/cgu/service-usage"
                    className="p-6 bg-white rounded-xl border border-blue-200 shadow-lg transition-all duration-200 group hover:shadow-xl hover:scale-105"
                  >
                    <div className="text-center">
                      <div className="flex justify-center items-center mx-auto mb-4 w-12 h-12 bg-blue-100 rounded-full transition-colors group-hover:bg-blue-200">
                        <Smartphone className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="mb-2 text-lg font-semibold text-gray-900">Utilisation du Service</h3>
                      <p className="mb-4 text-sm text-gray-600">
                        Règles détaillées d'utilisation, bonnes pratiques et activités interdites
                      </p>
                      <div className="flex justify-center items-center text-blue-600 group-hover:text-blue-700">
                        <span className="text-sm font-medium">Consulter</span>
                        <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>

                  <Link
                    to="/cgu/rights-responsibilities"
                    className="p-6 bg-white rounded-xl border border-green-200 shadow-lg transition-all duration-200 group hover:shadow-xl hover:scale-105"
                  >
                    <div className="text-center">
                      <div className="flex justify-center items-center mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full transition-colors group-hover:bg-green-200">
                        <Shield className="w-6 h-6 text-green-600" />
                      </div>
                      <h3 className="mb-2 text-lg font-semibold text-gray-900">Droits et Responsabilités</h3>
                      <p className="mb-4 text-sm text-gray-600">
                        Vos droits RGPD, obligations légales et cadre juridique applicable
                      </p>
                      <div className="flex justify-center items-center text-green-600 group-hover:text-green-700">
                        <span className="text-sm font-medium">Consulter</span>
                        <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>

                  <Link
                    to="/cgu/security-data-protection"
                    className="p-6 bg-white rounded-xl border border-indigo-200 shadow-lg transition-all duration-200 group hover:shadow-xl hover:scale-105"
                  >
                    <div className="text-center">
                      <div className="flex justify-center items-center mx-auto mb-4 w-12 h-12 bg-indigo-100 rounded-full transition-colors group-hover:bg-indigo-200">
                        <Lock className="w-6 h-6 text-indigo-600" />
                      </div>
                      <h3 className="mb-2 text-lg font-semibold text-gray-900">Sécurité et Protection</h3>
                      <p className="mb-4 text-sm text-gray-600">
                        Mesures de sécurité, chiffrement et certifications de protection des données
                      </p>
                      <div className="flex justify-center items-center text-indigo-600 group-hover:text-indigo-700">
                        <span className="text-sm font-medium">Consulter</span>
                        <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                </div>

                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-yellow-800">
                      <p className="mb-1 font-medium">Important :</p>
                      <p className="text-sm">
                        Ces pages détaillées font partie intégrante de nos Conditions Générales d'Utilisation. 
                        En utilisant Burok, vous acceptez l'ensemble de ces conditions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="p-8 text-center bg-gray-50 rounded-xl">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">
                Questions ou Préoccupations ?
              </h2>
              <p className="mb-6 text-gray-600">
                Si vous avez des questions concernant ces conditions, n'hésitez pas à nous contacter.
              </p>
              <a
                href="mailto:support@burok.app"
                className="inline-flex items-center px-6 py-3 font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-full transition-all duration-200 hover:shadow-lg hover:scale-105"
              >
                Contactez-nous
              </a>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default CGU;
